// @flow @format
import {put, select, cancelled, take, takeEvery} from 'redux-saga/effects';
import {eventChannel} from 'redux-saga';
import {PrivateApi} from 'lib/cloner-api';
import BlocksApi from 'lib/blocks-api';
import DownloadManager, * as cloader from 'lib/downloader';
import {
	addDownload,
	updateDownload,
	removeDownload,
	setThroughput,
	subscribeDownloads,
	updateDownloadsUsage,
	CREATE_DOWNLOAD,
	DELETE_DOWNLOAD,
	START_DOWNLOAD,
	PAUSE_DOWNLOAD,
	SAVE_DOWNLOAD,
} from '../reducers/entities/downloads';
import * as fs from 'lib/fs';

import type {State} from '../reducers';
import type {
	AsyncDownloadAction,
	CreateDownloadAction,
} from '../reducers/entities/downloads';

import type {DownloadSummary} from 'lib/downloader';

function createUpdatesChannel(manager: DownloadManager) {
	return eventChannel(emit => {
		const subscription = {
			onChange: (record: DownloadSummary) => emit(record),
			onSuccess: (record: DownloadSummary) => {
				manager.saveDownloadedFile(record.id);
			},
		};

		manager.subscribe(subscription);
		return () => manager.unsubscribe(subscription);
	});
}

function createThroughputUpdatesChannel(manager: DownloadManager) {
	return eventChannel(emit => {
		const subscription = {
			onUpdate: (throughtput: number) => emit(throughtput),
		};
		manager.subscribeTroughput(subscription);
		return () => manager.unsubscribeTroughput(subscription);
	});
}

function watchCreateDownload(api: BlocksApi, manager: DownloadManager) {
	return function*(action: CreateDownloadAction): Generator<*, void, *> {
		const state: State = yield select();
		const {repositories, files, downloads} = state.entities;

		if (downloads.entities[action.id]) {
			return;
		}

		const repository = repositories.entities[action.repositoryId];
		const includeDeleted = files.showDeleted;

		try {
			const metafile = yield api.getMetafile(
				action.id,
				action.repositoryId
			);

			const requiredBytes = downloads.usedBytes + metafile.Size;
			if (requiredBytes >= downloads.grantedBytes) {
				throw new Error('Error: file exceed current quota');
			}

			const top = manager.top();

			const download = yield manager.addDownload(
				repository,
				metafile,
				includeDeleted
			);

			const quotaBefore = yield fs.queryUsageAndQuota();
			yield manager.append(download.id);
			const quotaAfter = yield fs.queryUsageAndQuota();

			let diff = quotaAfter.usedBytes - quotaBefore.usedBytes;
			if (download.isFile) {
				diff -= download.size;
			}

			yield put(
				updateDownloadsUsage(
					quotaAfter.grantedBytes,
					requiredBytes + diff
				)
			);

			yield put(addDownload(download.id, download));

			if (!top || (!manager.isRunning() && top.isFile)) {
				yield manager.start(download.id);
			}
		} catch (error) {
			console.error(`Failed to create download, ${error.message}`);
		}
	};
}

function watchDeleteDownload(manager: DownloadManager) {
	return function*(action: AsyncDownloadAction): Generator<*, void, *> {
		const state: State = yield select();
		const {downloads} = state.entities;

		const download = downloads.entities[action.id];
		if (!download) {
			return;
		}

		try {
			const quotaBefore = yield fs.queryUsageAndQuota();
			yield manager.removeDownload(action.id);
			const quotaAfter = yield fs.queryUsageAndQuota();

			let diskSize = download.diskSize;

			if (Number.isNaN(diskSize)) {
				diskSize = 0;
			}

			const diff =
				quotaBefore.usedBytes - quotaAfter.usedBytes - diskSize;

			yield put(
				updateDownloadsUsage(
					quotaAfter.grantedBytes,
					downloads.usedBytes - download.size - diff
				)
			);
			yield put(removeDownload(action.id));
		} catch (error) {
			console.error(`Error: failed to remove download, ${error.message}`);
		}
	};
}

function watchStartDownload(manager: DownloadManager) {
	return function*(action: AsyncDownloadAction): Generator<*, void, *> {
		yield manager.start(action.id);
	};
}

function watchPauseDownload(manager: DownloadManager) {
	return function*(action: AsyncDownloadAction): Generator<*, void, *> {
		yield manager.pause(action.id);
	};
}

function watchSaveDownload(manager: DownloadManager) {
	return function*(action: AsyncDownloadAction): Generator<*, void, *> {
		yield manager.saveDownloadedFile(action.id);
	};
}

function* watchThroughputChange(throughtput: number): Generator<*, void, *> {
	yield put(setThroughput(throughtput));
}

function makeSaga(
	user: string,
	token: string,
	server: string,
	api: PrivateApi,
	blocksApi: BlocksApi
) {
	return function*(): Generator<*, *, *> {
		let manager: ?DownloadManager;

		try {
			yield fs.requestQuota(DownloadManager.requiredQuota);

			let {grantedBytes, usedBytes} = yield fs.queryUsageAndQuota();

			if (grantedBytes === 0) {
				throw new Error('Error: no storage bytes granted');
			}

			const fileSystem = yield fs.getPersistentFileSystem(grantedBytes);
			const dirEntry = yield fs.getDirectoryEntry(fileSystem, user);

			manager = new DownloadManager(user, token, server, dirEntry);

			// remove all completed and failed downloads
			let downloads = yield manager.allDownloads(
				cloader.DOWNLOAD_COMPLETED_STATUS,
				cloader.DOWNLOAD_FAILED_STATUS
			);

			for (const download of downloads) {
				yield manager.removeDownload(download.id);
			}

			// append to the queue all completed and failed downloads
			downloads = yield manager.allDownloads(
				cloader.DOWNLOAD_RUNNING_STATUS,
				cloader.DOWNLOAD_PAUSING_STATUS,
				cloader.DOWNLOAD_PAUSED_STATUS,
				cloader.DOWNLOAD_SETUP_STATUS,
				cloader.DOWNLOAD_RETRY_STATUS
			);

			downloads = downloads.sort(
				(lhs, rhs) => rhs.downloaded - lhs.downloaded
			);

			({grantedBytes, usedBytes} = yield fs.queryUsageAndQuota());
			for (const download of downloads) {
				yield manager.append(download);

				let diskSize = download.diskSize;

				if (Number.isNaN(diskSize)) {
					diskSize = 0;
				}

				// we need to add the not downloaded part of the file
				// to keep track of the possible quota used.
				usedBytes += download.size - diskSize;

				yield put(
					addDownload(download.id, cloader.downloadSummary(download))
				);
			}

			yield put(subscribeDownloads());
			yield put(updateDownloadsUsage(grantedBytes, usedBytes));

			yield takeEvery(
				CREATE_DOWNLOAD,
				watchCreateDownload(blocksApi, manager)
			);
			yield takeEvery(DELETE_DOWNLOAD, watchDeleteDownload(manager));
			yield takeEvery(START_DOWNLOAD, watchStartDownload(manager));
			yield takeEvery(PAUSE_DOWNLOAD, watchPauseDownload(manager));
			yield takeEvery(SAVE_DOWNLOAD, watchSaveDownload(manager));

			const throughtputUpdatesChannel = yield createThroughputUpdatesChannel(
				manager
			);
			yield takeEvery(throughtputUpdatesChannel, watchThroughputChange);

			const updatesChannel = yield createUpdatesChannel(manager);
			while (true) {
				const download = yield take(updatesChannel);
				yield put(updateDownload(download.id, download));
			}
		} catch (error) {
			console.error(error);
		} finally {
			if (yield cancelled()) {
				if (manager) {
					const top = manager.top();
					if (top) {
						yield manager.pause(top.id);
					}
				}
			}
		}
	};
}

export default makeSaga;



// WEBPACK FOOTER //
// ./src/sagas/downloads.js